<script setup lang="ts">
import { NButton } from 'naive-ui'
import { useAttrs } from 'vue'

const props = withDefaults(defineProps<{
  type?: 'default' | 'tertiary' | 'primary' | 'success' | 'info' | 'warning' | 'error'
  size?: 'tiny' | 'small' | 'medium' | 'large'
  color?: string
  ghost?: boolean
  text?: boolean
  block?: boolean
  round?: boolean
  circle?: boolean
  dashed?: boolean
  disabled?: boolean
  loading?: boolean
  keyboard?: boolean
  focusable?: boolean
  quaternary?: boolean
  tag?: string
  attrType?: 'button' | 'submit' | 'reset'
  nativeFocusBehavior?: boolean
  onUpdateLoading?: (loading: boolean) => void
  onClick?: (e: MouseEvent) => void
  onFocus?: (e: FocusEvent) => void
  onBlur?: (e: FocusEvent) => void
}>(), {
  type: 'default',
  size: 'medium',
  color: undefined,
  ghost: false,
  text: false,
  block: false,
  round: false,
  circle: false,
  dashed: false,
  disabled: false,
  loading: false,
  keyboard: false,
  focusable: true,
  quaternary: false,
  tag: 'button',
  attrType: 'button',
  nativeFocusBehavior: false,
})

const emit = defineEmits<{
  'update:loading': [loading: boolean]
  'click': [e: MouseEvent]
  'focus': [e: FocusEvent]
  'blur': [e: FocusEvent]
}>()

const attrs = useAttrs()

const onUpdateLoading = (loading: boolean) => {
  emit('update:loading', loading)
}

const onClick = (e: MouseEvent) => {
  emit('click', e)
}

const onFocus = (e: FocusEvent) => {
  emit('focus', e)
}

const onBlur = (e: FocusEvent) => {
  emit('blur', e)
}
</script>

<template>
  <NButton
    :type="type"
    :size="size"
    :color="color"
    :ghost="ghost"
    :text="text"
    :block="block"
    :round="round"
    :circle="circle"
    :dashed="dashed"
    :disabled="disabled"
    :loading="loading"
    :keyboard="keyboard"
    :focusable="focusable"
    :quaternary="quaternary"
    :tag="tag as keyof HTMLElementTagNameMap"
    :attr-type="attrType"
    :native-focus-behavior="nativeFocusBehavior"
    v-bind="attrs"
    @update:loading="onUpdateLoading"
    @click="onClick"
    @focus="onFocus"
    @blur="onBlur"
  >
    <template #icon>
      <slot name="icon" />
    </template>
    <template #default>
      <slot />
    </template>
  </NButton>
</template>

<style scoped>
/* Custom styles can be added here if needed */
</style>
